import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import { usePathBasedQueryString } from '@lib/usePathBasedQueryString';

import { decode } from '../lib/base64';
import { InvalidPage } from '../components/errors/invalidPage';
import { DeviceAnomalyExaminationPage } from '../containers/core/deviceAnomalies/examination/deviceAnomalyExaminationPage';
import { isAnomalyType } from '../containers/core/deviceAnomalies/compositions/anomalyTypePicker';
import { anomalySections } from '../containers/core/deviceAnomalies/examination/compositions/sections';
import { useWorldRoutes } from './parts/allWorldRoutes';

export const CoreDeviceAnomalyExaminationRoute = () => {
  // Matches the pattern every other query-string-driven route in this file uses (e.g.
  // core-access-point-route.tsx, core-application-route.tsx) rather than reading useLocation's
  // search directly - see usePathBasedQueryString for why (freezes the remembered query string
  // while this route isn't the active StateSwitch tab, rather than picking up an unrelated tab's).
  const { queryString: search } = usePathBasedQueryString(/\/core\/anomalies-beta\/details\/examination/);
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const encodedId = searchParams.get('id');
  const deviceId = encodedId ? decode(encodedId) : undefined;
  const dateParam = searchParams.get('date');
  // A completely absent `date` (`null`) means "default to the latest anomaly day", resolved by
  // DeviceAnomalyExaminationPage itself from its own top chart data (see getLatestAnomalyDate) -
  // used by the top-level anomalies table's links. Anything else supplied, including an explicit
  // empty value, must still be a finite number, exactly like id/type below.
  const hasDateParam = dateParam !== null;
  const date = hasDateParam ? Number(dateParam) : undefined;
  const dateIsValid = !hasDateParam || (dateParam !== '' && Number.isFinite(date));
  const type = searchParams.get('type');
  const routes = useWorldRoutes();
  const { t } = useTranslation('redirect');

  const notFound = (
    <InvalidPage
      message={t('DEVICE_ANOMALY_EXAMINATION_NOT_FOUND')}
      redirect={{
        message: t('REDIRECT_TO_ANOMALY_DETECTION_BETA'),
        path: routes.core.anomalyDetectionBeta
      }}
    />
  );

  // A type with no registered section (compositions/sections/index.ts) is treated as invalid so
  // sibling phase 2 anomaly types degrade gracefully to this redirect until their story lands,
  // rather than the shell rendering a top chart with no lower panel underneath it.
  if (!deviceId || !dateIsValid || !type || !isAnomalyType(type) || !anomalySections[type]) {
    return notFound;
  }

  return <DeviceAnomalyExaminationPage deviceId={deviceId} anomalyType={type} initialDate={date} />;
};
